import { useState } from "react"
import { Head } from "@inertiajs/react"
import { RockGrid } from "@/components/planning/RockCard"
import { KpiScorecard } from "@/components/planning/KpiScorecard"
import { CadenceHeatmap } from "@/components/planning/CadenceHeatmap"

// Dev-only preview of the three progress visuals we're planning to ship.
// All data is mocked client-side. Edits work locally but don't hit the server.
// See: docs/plans/2026-04-20-002-feat-member-dashboard-progress-visuals-plan.md

const QUARTER_START = "2026-04-01T00:00:00Z"
const QUARTER_END = "2026-06-30T23:59:59Z"

const INITIAL_ROCKS = [
  {
    id: "r1",
    title: "Launch new service line",
    status: "on_track",
    progressPct: 65,
    milestones: [
      { id: "m1", label: "Validate with 5 current clients", done: true },
      { id: "m2", label: "Build pilot offer deck", done: true },
      { id: "m3", label: "Soft launch to 3 prospects", done: false },
      { id: "m4", label: "Close first paid engagement", done: false },
    ],
    createdAt: "2026-04-02T09:00:00Z",
    updatedAt: "2026-04-18T14:22:00Z",
    result: null,
  },
  {
    id: "r2",
    title: "Hire second operations lead",
    status: "at_risk",
    progressPct: 25,
    milestones: [],
    createdAt: "2026-04-05T10:00:00Z",
    updatedAt: "2026-04-15T08:30:00Z",
    result: null,
  },
  {
    id: "r3",
    title: "Complete fundraising round",
    status: "blocked",
    progressPct: 40,
    milestones: [
      { id: "m5", label: "Investor intro calls", done: true },
      { id: "m6", label: "Deck reviewed by board", done: true },
      { id: "m7", label: "Term sheet signed", done: false },
      { id: "m8", label: "Wire received", done: false },
      { id: "m9", label: "Board approval on deployment", done: false },
    ],
    createdAt: "2026-04-01T09:00:00Z",
    updatedAt: "2026-04-10T11:45:00Z",
    result: null,
  },
  {
    id: "r4",
    title: "Personal prayer & Scripture cadence",
    status: "on_track",
    progressPct: 10,
    milestones: [],
    // Created near the start of the quarter; now Q2 is ~40% elapsed but
    // this rock is at 10% — triggers the behind-pace amber arc.
    createdAt: "2026-03-28T09:00:00Z",
    updatedAt: "2026-04-05T07:00:00Z",
    result: null,
  },
]

const KPIS = [
  {
    id: "kpi1",
    name: "Monthly Recurring Revenue",
    target: "$150k",
    series: buildSeries([null, null, "plus", "equal", "plus", "plus", "equal", "plus", "plus", "plus", "equal", "plus"]),
  },
  {
    id: "kpi2",
    name: "New Engagements Closed",
    target: "3/mo",
    series: buildSeries(["equal", "minus", "equal", "plus", "equal", "plus", "minus", "equal", "plus", "equal", "plus", "plus"]),
  },
  {
    id: "kpi3",
    name: "Team Gross Margin",
    target: "55%",
    unit: null,
    series: buildSeries(["minus", "minus", "equal", "minus", "equal", "minus", "equal", "plus", "equal", "minus", "minus", "equal"]),
  },
]

const KOIS = [
  {
    id: "koi1",
    name: "Family Sabbath Kept",
    target: "4 of 4 weeks",
    series: buildSeries(["plus", "plus", "equal", "plus", "plus", "plus", "equal", "plus", "plus", "plus", "plus", "plus"]),
  },
  {
    id: "koi2",
    name: "Kingdom Stories Shared",
    target: "1/week",
    series: buildSeries(["equal", "plus", "equal", "plus", "equal", "equal", "plus", "equal", "plus", "plus", "equal", "plus"]),
  },
]

const CADENCE_CELLS = buildCadence()

function buildSeries(statuses) {
  const now = new Date()
  return statuses.map((status, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (statuses.length - 1 - i), 1)
    return {
      monthKey: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      status,
    }
  })
}

function buildCadence() {
  const now = new Date()
  const monday = new Date(now)
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7))
  const cells = []
  for (let i = 51; i >= 0; i--) {
    const d = new Date(monday)
    d.setDate(d.getDate() - i * 7)
    // Mock realistic engagement: strong in last 8 weeks, spotty before
    const isRecent = i < 8
    const isMonthlyCheckin = i % 4 === 0 && i < 20
    const rockUpdates = isRecent ? (Math.random() < 0.7 ? Math.floor(Math.random() * 4) + 1 : 0)
                                 : (Math.random() < 0.3 ? Math.floor(Math.random() * 2) + 1 : 0)
    let intensity = 0
    if (rockUpdates >= 2 || isMonthlyCheckin) intensity = 2
    else if (rockUpdates === 1) intensity = 1
    cells.push({
      weekStart: d.toISOString().slice(0, 10),
      intensity,
      rockUpdates,
      monthlyCheckin: isMonthlyCheckin,
    })
  }
  return cells
}

export default function PlanningVisualsDemo() {
  const [rocks, setRocks] = useState(INITIAL_ROCKS)
  const [readOnly, setReadOnly] = useState(false)

  const updateRock = (rockId, field, value) => {
    setRocks((prev) =>
      prev.map((r) =>
        r.id === rockId ? { ...r, [field === "progress_pct" ? "progressPct" : field]: value, updatedAt: new Date().toISOString() } : r,
      ),
    )
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Head title="Planning Visuals Demo" />

      <header className="bg-white border-b border-zinc-200 px-6 py-5">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500">Dev preview · not connected to DB</p>
            <h1 className="font-display text-2xl text-zinc-900">Member dashboard — progress visuals</h1>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-zinc-700">
            <input
              type="checkbox"
              checked={readOnly}
              onChange={(e) => setReadOnly(e.target.checked)}
              className="accent-kf-red"
            />
            Show as coach (read-only)
          </label>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-xl text-zinc-900">This quarter's rocks</h2>
            <p className="text-xs text-zinc-500">Q2 2026 · ends Jun 30</p>
          </div>
          <RockGrid
            rocks={rocks}
            quarterStartAt={QUARTER_START}
            quarterEndAt={QUARTER_END}
            readOnly={readOnly}
            onUpdate={updateRock}
          />
          <p className="text-xs text-zinc-500 mt-3 italic">
            Try: click a status pill to change it · click "Update progress" on a card · toggle a milestone ·
            watch the amber pacing dial appear when "on track" falls behind.
          </p>
        </section>

        <section className="space-y-6">
          <KpiScorecard items={KPIS} heading="Annual KPIs" />
          <KpiScorecard items={KOIS} heading="Annual KOIs" />
        </section>

        <section>
          <CadenceHeatmap cells={CADENCE_CELLS} />
          <p className="text-xs text-zinc-500 mt-3 italic">
            Hover a cell to see the week's activity. The current week pulses.
          </p>
        </section>

        <section className="rounded-lg border border-zinc-200 bg-white p-5 text-sm text-zinc-600">
          <h3 className="font-semibold text-zinc-900 mb-2">What you're looking at</h3>
          <ul className="space-y-1.5 list-disc pl-5">
            <li>
              <strong>Rock cards:</strong> progress ring colored by status tag, amber behind-pace arc
              when on-track rocks fall behind the linear calendar pace, milestone-driven % when
              milestones exist.
            </li>
            <li>
              <strong>Scorecard:</strong> trailing 12 months of ± signals from existing monthly check-ins.
              No new numeric tracking needed.
            </li>
            <li>
              <strong>Cadence:</strong> 52 weekly cells. Empty / one update / active (2+ or monthly
              check-in). Data source: rock <code>updated_at</code> + <code>MonthlyCheckIn.completed_at</code>.
            </li>
            <li>
              <strong>Coach toggle</strong> in the top-right hides all edit affordances.
            </li>
          </ul>
        </section>
      </main>
    </div>
  )
}
